import { ICustomer } from 'shared-types/SharedTypes';
import PouchLoadService from 'external-services/pouch-load-service';
import { AxiosInstance } from 'axios';
import NetworkRequestsService from 'external-services/network-requests-service';
import CustomerNormaliseService from 'external-services/customer-normalise-service';
import NetworkConnectionService from 'external-services/network-connection-service';
import { ApiStatus } from 'shared-types/DiaryTypes';
import { appURLS } from 'appRoot/app.constants';

const NS = 'CustomerLookupService';

const api: AxiosInstance = NetworkRequestsService.getGlobalAPI();

export const getApiInstanceForTests = (): AxiosInstance => api as AxiosInstance;
interface IPartialAuthService {
  roleCheckLogout: () => void;
  showServerBusyMessage: () => void;
}
class CustomerLookupService {
  private AuthService: IPartialAuthService;
  public setup(AuthService: IPartialAuthService): void {
    this.AuthService = AuthService;
  }

  private venueCustomersUrl = '';
  private accountCustomersUrl = '';

  private getCustomerFromCouchDB(id: string): Promise<ICustomer | null> {
    return PouchLoadService.getDbLocal()
      .get(id)
      .then((customer: unknown) => {
        return CustomerNormaliseService.normalizeModelToView(customer as ICustomer);
      })
      .catch((err: any) => {
        console.warn(err);
        if (err?.response?.status === 401) {
          // expired token
          this.AuthService.roleCheckLogout();
        } else {
          if (err?.response?.status === 503) {
            this.AuthService.showServerBusyMessage();
          }
          NetworkConnectionService.setApiStatus(ApiStatus.isOffline);
        }
        return Promise.resolve(null);
      });
  }

  /**
   * Customers Online account customer by Id, in offline mode look up into PouchDB for the customer
   * @param {String} id
   * WARNING!!!! venue users with roles Roles.VenueStaff or Roles.VenueManager will not be authenticated for this request
   * and there was also a bug that caused manager notes to be null with it (5 Feb 2024).
   */
  public getAccountCustomerById(id: string): Promise<ICustomer> {
    if (NetworkConnectionService.isOnline()) {
      return new Promise((resolve) => {
        api.get(this.accountCustomersUrl + '/' + id + '?includeTagSharedData=true').then((response: { data: ICustomer }) => {
          resolve(CustomerNormaliseService.normalizeModelToView(response.data, true));
        }).catch(error => {
          console.error('CustomerLookupService.getById', id, error);
          resolve(null);
        });
      });
    } else {
      return this.getCustomerFromCouchDB(id);
    }
  }

  /**
   * Customers Online venue in offline mode look up into PouchDB for the customer
   * @param {String} id
   */
  public async getById(id: string, byVenue = true): Promise<ICustomer> {
    if (NetworkConnectionService.isOnline()) {
      try {
        const url = byVenue ? this.venueCustomersUrl : this.accountCustomersUrl;
        const response: { data: ICustomer } = await api.get(
          url + '/' + id
        );
        return CustomerNormaliseService.normalizeModelToView(response.data, true);
      } catch (error) {
        console.error('CustomerLookupService.getById', id, error);
        return null;
      }
    } else {
      return this.getCustomerFromCouchDB(id);
    }
  }

  /**
   * Customers Online venue in offline mode look up into PouchDB for the customers
   * @param {Array<String>} ids
   */
  public getByIds(ids: string[], byVenue = true): Promise<ICustomer[]> {
    if (!ids || !ids.length) {
      return Promise.resolve([]);
    }

    if (NetworkConnectionService.isOnline()) {
      // Online request chuncked to not exceed browsers URI length limit
      const requests: Promise<ICustomer[]>[] = [];
      const idsChunkSize = 40;
      const url = byVenue ? this.venueCustomersUrl : this.accountCustomersUrl;
      for (let i = 0; i < ids.length; i += idsChunkSize) {
        const temp: string[] = [...ids];
        const promise = new Promise<ICustomer[]>((resolve, reject) => {
          api
            .get(
              url + '/ids', {
                  params: {
                    ids: temp.splice(i, idsChunkSize).join(','),
                  }
              }
            )
            .then((response: { data: ICustomer[] }) => {
              const customers: ICustomer[] = [];
              for (const i in response.data) {
                customers.push(
                  CustomerNormaliseService.normalizeModelToView(response.data[i], true)
                );
              }
              resolve(customers);
            })
            .catch((error) => {
              reject(error);
            });
        });
        requests.push(promise);
      }
      return Promise.all(requests).then((results: ICustomer[][]) => {
        let customers: ICustomer[] = [];
        for (const result of results) {
          customers = customers.concat(result);
        }
        return customers;
      });
    }

    return Promise.resolve(
      PouchLoadService.getDbLocal()
        .allDocs({
          include_docs: true,
          attachments: false,
          keys: ids,
        })
        .then((customers: unknown) => {
          return (customers as { rows: { doc: ICustomer }[] }).rows.map(
            (item: { doc: ICustomer }) => CustomerNormaliseService.normalizeModelToView(item.doc)
          );
        })
    );
  }

  public getVenueVisitHistory(customerId: string): Promise<string[]> {
    return new Promise((resolve) => {
      api
        .get(
          this.venueCustomersUrl + '/' + customerId + '/venue-visit-history'
        )
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          console.error('Customer venue history', error);
          resolve(null);
        });
    });
  }

  public setVenueCustomersUrl(venueId: number) {
    this.venueCustomersUrl = `${appURLS.API}/restaurants/venues/${venueId}/customers`;
    return this.venueCustomersUrl;
  }

  public setAccountCustomersUrl(accountId: string) {
    this.accountCustomersUrl = `${appURLS.API}/accounts/${accountId}/customers`;
    return this.accountCustomersUrl;
  }
}

const instance = new CustomerLookupService();
export default instance as CustomerLookupService;
